import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { providerAssignmentActions } from '../../../../../../store';
import { PARENT_GUARDIAN_CLINICAL_RELATIONSHIPS } from './constants';
import {
  getInformationBcbaEntries,
  getInformationBtEntries,
  getInformationParentGuardianEntries,
} from './utils';

import type { AppDispatch, RootState } from '../../../../../../store';
import type { IProviderAssignment } from '../../../../../../utils/types';
import type {
  ParentGuardianAssignmentsResult,
  UseTreatmentPlanV3InformationResult,
} from './types';

export default function useTreatmentPlanV3Information(): UseTreatmentPlanV3InformationResult {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const informationPage = useSelector((state: RootState) => state.treatmentPlanV3.informationPage);
  const hasLoadError = useSelector(
    (state: RootState) => state.treatmentPlanV3.informationPageError != null,
  );
  const clientId = informationPage?.client?.id ?? null;
  const [loadedClientId, setLoadedClientId] = useState<number | null>(null);
  const [failedClientId, setFailedClientId] = useState<number | null>(null);
  const [parentGuardianAssignments, setParentGuardianAssignments] =
    useState<ParentGuardianAssignmentsResult | null>(null);

  useEffect(() => {
    if (clientId == null) return;

    let cancelled = false;
    const request = dispatch(
      providerAssignmentActions.getList({
        getAccessTokenSilently,
        active_only: true,
        client_id: clientId,
        clinical_relationship: PARENT_GUARDIAN_CLINICAL_RELATIONSHIPS,
      }),
    );

    // Dispatched thunks resolve with a rejected action, so unwrap() is what rejects on failure.
    request
      .unwrap()
      .then((assignments: IProviderAssignment[]) => {
        if (cancelled) return;
        setParentGuardianAssignments({ clientId, assignments });
        setFailedClientId(null);
        setLoadedClientId(clientId);
      })
      .catch(() => {
        if (cancelled) return;
        setParentGuardianAssignments(null);
        setLoadedClientId(null);
        setFailedClientId(clientId);
      });

    return () => {
      cancelled = true;
      request.abort();
    };
  }, [clientId, dispatch, getAccessTokenSilently]);

  const parentGuardianAssignmentsReady = clientId == null || loadedClientId === clientId;
  const parentGuardianLoadFailed = clientId != null && failedClientId === clientId;
  const parentGuardianEntries =
    parentGuardianAssignments?.clientId === clientId && loadedClientId === clientId
      ? getInformationParentGuardianEntries(parentGuardianAssignments.assignments, clientId)
      : [];

  return {
    client: informationPage?.client ?? null,
    insurance: informationPage?.insurance ?? null,
    btEntries: getInformationBtEntries(informationPage),
    bcbaEntries: getInformationBcbaEntries(informationPage),
    parentGuardianEntries,
    parentGuardianLoadFailed,
    hasLoaded:
      informationPage !== null && (parentGuardianAssignmentsReady || parentGuardianLoadFailed),
    hasLoadError,
  };
}
